<div class="it-panel">

  <!-- Header -->
  <div class="it-header">
    <!-- Every step can go back, the review included: nothing is written until Import, so the
         period, accounts or file can be changed and fetched again. Not while an import runs. -->
    <button class="it-back-btn" *ngIf="!showTabs"
            (click)="goBack()" [disabled]="isSaving" title="Back">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="15 18 9 12 15 6"></polyline>
      </svg>
    </button>
    <h2 class="it-title">{{ panelTitle }}</h2>
    <button class="it-close-btn" (click)="close()" title="Close">&times;</button>
  </div>

  <!-- SD-1207: starting an import and looking back at past ones, side by side. The history
       used to be a tab on the Transactions page; it belongs with the thing it records. -->
  <div class="it-tabs" role="tablist" aria-label="Import transactions" *ngIf="showTabs">
    <button type="button" role="tab" class="it-tab" [class.it-tab--on]="step === 'method'"
            [attr.aria-selected]="step === 'method'" (click)="step = 'method'">Import</button>
    <button type="button" role="tab" class="it-tab" [class.it-tab--on]="step === 'history'"
            [attr.aria-selected]="step === 'history'" (click)="step = 'history'">History</button>
  </div>

  <!-- ══════════════════════════════════════════════════════════
       STEP: HISTORY  (SD-1207)
  ══════════════════════════════════════════════════════════ -->
  <div *ngIf="step === 'history'" class="it-body">
    <app-import-history [organisationId]="organisationId"></app-import-history>
  </div>

  <!-- ══════════════════════════════════════════════════════════
       STEP: METHOD SELECTION
  ══════════════════════════════════════════════════════════ -->
  <div *ngIf="step === 'method'" class="it-body it-body--method">

    <p class="it-subtitle">Choose where the transactions come from. You review them before anything is imported.</p>

    <!-- SD-1207: shown only to organisations holding the bank-feeds module, and DISABLED
         rather than hidden when the module is held but the feed cannot be used (it is
         US-only). A missing button is a support ticket; a disabled one with the reason
         on it is an answer. -->
    <div class="it-method-card" *ngIf="hasBankFeeds"
      [class.it-method-card--disabled]="!isBankFeedAvailable"
      (click)="isBankFeedAvailable && selectMethod('bank')" role="button"
      [attr.tabindex]="isBankFeedAvailable ? 0 : -1"
      [attr.aria-disabled]="!isBankFeedAvailable"
      (keydown.enter)="isBankFeedAvailable && selectMethod('bank')">
      <div class="it-method-icon it-method-icon--glyph">
        <!-- The app's own bank glyph, the same path Settings › Banking draws for a bank
             account. The sidebar's Transactions icon is a pair of transfer arrows, which
             says "moving money between things" rather than "a bank". -->
        <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M2 6l6-3.5L14 6M3 13.5h10M4 6.5v5M6.67 6.5v5M9.33 6.5v5M12 6.5v5"
                stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </div>
      <div class="it-method-text">
        <div class="it-method-name">Import From Bank Feed</div>
        <!-- One line, always populated: "checking", the pitch, or the reason it cannot be
             used. A card with a title and nothing else is a dead control. -->
        <div class="it-method-desc">{{ bankFeedDescription }}</div>
      </div>
      <svg class="it-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        stroke-width="2" *ngIf="isBankFeedAvailable">
        <polyline points="9 18 15 12 9 6"></polyline>
      </svg>
    </div>

    <div class="it-method-card" *ngIf="isXeroAccount" (click)="selectMethod('xero')" role="button" tabindex="0"
      (keydown.enter)="selectMethod('xero')">
      <div class="it-method-icon">
        <img [src]="xeroLogo" alt="Xero" width="52" height="52" />
      </div>
      <div class="it-method-text">
        <div class="it-method-name">
          Import From Xero
          <!-- The daily sync lives inside this step; its state shows here so it is not forgotten. -->
          <span class="it-sync-pill it-sync-pill--active" *ngIf="dailySyncMode === 'active'">
            <span class="it-sync-dot"></span>Daily sync on
          </span>
        </div>
        <div class="it-method-desc">Pull bank transactions directly from your Xero account, once or every day</div>
      </div>
      <svg class="it-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        stroke-width="2">
        <polyline points="9 18 15 12 9 6"></polyline>
      </svg>
    </div>

    <div class="it-method-card" (click)="selectMethod('spreadsheet')" role="button" tabindex="0"
      (keydown.enter)="selectMethod('spreadsheet')">
      <div class="it-method-icon">
        <img [src]="spreadsheetLogo" alt="Spreadsheet" width="52" height="52" />
      </div>
      <div class="it-method-text">
        <div class="it-method-name">Import From Spreadsheet</div>
        <div class="it-method-desc">Upload a CSV file with your bank transactions</div>
      </div>
      <svg class="it-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        stroke-width="2">
        <polyline points="9 18 15 12 9 6"></polyline>
      </svg>
    </div>

  </div>

  <!-- ══════════════════════════════════════════════════════════
       STEP: BANK FEED  (SD-1207)
  ══════════════════════════════════════════════════════════ -->
  <div *ngIf="step === 'bank'" class="it-body">

    <p class="it-subtitle">
      Choose the account to import from. You&rsquo;ll review the transactions before anything
      is imported.
    </p>

    <div class="it-feed-error" *ngIf="bankError">{{ bankError }}</div>

    <div class="it-feed-loading" *ngIf="bankLoading">Loading your bank feeds&hellip;</div>

    <!-- No feeds yet -->
    <div class="it-feed-empty" *ngIf="!bankLoading && !bankFeeds.length">
      <p>No bank accounts are connected yet.</p>
      <button type="button" class="it-primary-btn" (click)="connectBank()" [disabled]="bankBusy">
        {{ bankBusy ? 'Opening&hellip;' : 'Connect a bank account' }}
      </button>
    </div>

    <!-- Connected feeds. A new one is connected from the account menu on Transactions. -->
    <ng-container *ngIf="!bankLoading && bankFeeds.length">
    <div class="it-field-group">
      <label class="it-label" for="feed-from">Import from</label>
      <input type="date" class="it-feed-input" id="feed-from" [(ngModel)]="bankImportFrom"
             [ngModelOptions]="{standalone: true}" />
      <p class="it-feed-hint">Leave blank to go back as far as your bank allows &mdash; about 180 days.</p>
    </div>

    <div class="it-feed-list">
      <div class="it-feed" *ngFor="let feed of bankFeeds">

        <!-- Ready to import: the whole card opens the review, like the method cards. -->
        <button type="button" class="it-feed-head it-feed-head--action" *ngIf="canReviewFeed(feed); else feedHead"
                (click)="reviewBankFeed(feed)" [disabled]="bankBusy">
          <span class="it-feed-name">
            {{ feed.institutionName }}
            <span class="it-feed-account">
              {{ feed.displayName }} &middot;&middot;&middot;&middot;{{ feed.last4 }} &rarr; {{ feed.accountName }}
            </span>
          </span>
          <span class="it-feed-head-end">
            <span class="it-feed-health" [ngClass]="'it-feed-health--' + feed.health">
              {{ bankReviewingId === feed.id ? 'Loading…' : bankHealthLabel(feed) }}
            </span>
            <svg class="it-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="2" aria-hidden="true">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </span>
        </button>

        <ng-template #feedHead>
          <div class="it-feed-head">
            <div class="it-feed-name">
              {{ feed.institutionName }}
              <span class="it-feed-account">{{ feed.displayName }} &middot;&middot;&middot;&middot;{{ feed.last4 }}</span>
            </div>
            <span class="it-feed-health" [ngClass]="'it-feed-health--' + feed.health">
              {{ bankHealthLabel(feed) }}
            </span>
          </div>
        </ng-template>

        <!-- Stripe cannot reach the account any more. Only the user can fix it, by
             re-authenticating at their bank. -->
        <div class="it-feed-body" *ngIf="feed.health === 'needs_reconnect'">
          <p class="it-feed-note">
            Your bank has stopped sharing this account &mdash; usually after a password
            change. Reconnect to start it again.
          </p>
          <button type="button" class="it-primary-btn" (click)="connectBank(feed.id)" [disabled]="bankBusy">
            Reconnect
          </button>
        </div>

        <!-- Linked at the bank, but nobody has said where its transactions belong. -->
        <div class="it-feed-body" *ngIf="feed.health !== 'needs_reconnect' && !feed.isMapped">
          <!-- Match to an account that already exists... -->
          <ng-container *ngIf="bankAccountList.length">
            <label class="it-label" [attr.for]="'feed-account-' + feed.id">
              Which account is this?
            </label>
            <div class="it-feed-map">
              <select class="it-feed-select" [id]="'feed-account-' + feed.id"
                      [class.it-feed-select--empty]="!bankAccountChoice[feed.id]"
                      [(ngModel)]="bankAccountChoice[feed.id]" [ngModelOptions]="{standalone: true}">
                <option [ngValue]="undefined">Choose an account&hellip;</option>
                <option *ngFor="let account of bankAccountList" [ngValue]="account.accountID">
                  {{ account.name }}
                </option>
              </select>
              <button type="button" class="it-feed-link-btn"
                      (click)="mapBankFeed(feed)"
                      [disabled]="bankBusy || !bankAccountChoice[feed.id]">
                Link
              </button>
            </div>
          </ng-container>

          <!-- ...or make one for it. Organisations with Xero or QuickBooks only get the
               first: their accounts come from the accounting system. -->
          <ng-container *ngIf="isNoAccounting">
            <div class="it-feed-or" *ngIf="bankAccountList.length"><span>or</span></div>
            <button type="button" class="it-secondary-btn it-feed-create-btn"
                    (click)="createAccountForFeed(feed)" [disabled]="bankBusy">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M7 2.5v9M2.5 7h9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
              </svg>
              Create &ldquo;{{ suggestedAccountName(feed) }}&rdquo;
            </button>
            <p class="it-feed-hint">Adds it to your bank accounts and links this feed to it.</p>
          </ng-container>

          <p class="it-feed-note" *ngIf="!bankAccountList.length && !isNoAccounting">
            There are no bank accounts to link this feed to yet.
          </p>
        </div>

        <!-- Ready, but daily updates are off: worth saying before they rely on it. -->
        <p class="it-feed-note it-feed-note--inline" *ngIf="canReviewFeed(feed) && !feed.subscriptionActive">
          <span class="it-feed-warn">Daily updates are not switched on for this feed yet.</span>
        </p>
      </div>
    </div>
    </ng-container>

    <div class="it-feed-result" *ngIf="bankImportResult">
      {{ describeImport(bankImportResult) }}
    </div>
  </div>

  <!-- ══════════════════════════════════════════════════════════
       STEP: XERO CONFIGURATION
  ══════════════════════════════════════════════════════════ -->
  <div *ngIf="step === 'xero'" class="it-body it-xero">

    <!-- Form (period + bank accounts), with Fetch in the footer — hidden during fetch -->
    <ng-container *ngIf="!isXeroLoading">
    <p class="it-subtitle">
      Choose a period and the bank accounts to pull from Xero. You&rsquo;ll review the transactions
      before anything is imported.
    </p>

    <section class="it-xero-section">
      <div class="it-xero-section-head">
        <span class="it-xero-section-title">Period</span>
        <span class="it-xero-section-meta" *ngIf="xeroRangeLabel">{{ xeroRangeLabel }}</span>
      </div>
      <!-- Two columns: each "This" beside its "Last", and Custom range across the bottom. -->
      <div class="it-xero-periods" role="radiogroup" aria-label="Period">
        <button type="button" *ngFor="let p of periodOptions()" class="it-xero-period"
          role="radio" [attr.aria-checked]="dateRangeSelected === p"
          [class.selected]="dateRangeSelected === p" (click)="onPeriodSelect(p)">
          {{ p }}
        </button>
      </div>

      <!-- Custom range pickers -->
      <div class="it-custom-range" *ngIf="showCustomFilter">
        <div class="it-custom-range-row">
          <div class="it-custom-range-field">
            <span class="it-custom-range-label">From</span>
            <input type="date" class="it-custom-range-input"
              [(ngModel)]="customStartDate"
              (ngModelChange)="applyCustomRange()"
              (click)="openNativeDatePicker($event)"
              (focus)="openNativeDatePicker($event)" />
          </div>
          <div class="it-custom-range-field">
            <span class="it-custom-range-label">To</span>
            <input type="date" class="it-custom-range-input"
              [(ngModel)]="customEndDate"
              (ngModelChange)="applyCustomRange()"
              (click)="openNativeDatePicker($event)"
              (focus)="openNativeDatePicker($event)" />
          </div>
        </div>
        <div *ngIf="customRangeError" class="it-custom-range-error">{{ customRangeError }}</div>
      </div>
    </section>

    <section class="it-xero-section" appClickOutsideExcept (clickOutside)="bankDropdownOpen = false">
      <div class="it-xero-section-head">
        <span class="it-xero-section-title">Bank accounts</span>
        <span class="it-xero-section-meta" *ngIf="bankAccountList.length">
          {{ selectedBankAccounts.length }} of {{ bankAccountList.length }} selected
        </span>
      </div>

      <!-- Trigger -->
      <button class="it-acct-trigger" [class.open]="bankDropdownOpen"
        (click)="bankDropdownOpen = !bankDropdownOpen" type="button">
        <div class="it-acct-trigger-left">
          <ng-container *ngIf="selectedBankAccounts.length === 0">
            <span class="it-acct-placeholder">Select accounts&hellip;</span>
          </ng-container>
          <ng-container *ngIf="selectedBankAccounts.length > 0">
            <span class="it-acct-chip" *ngFor="let a of selectedBankAccounts">
              <span class="it-acct-chip-icon" aria-hidden="true">
                <ng-container *ngIf="acctKind(a) === 'card'"><svg viewBox="0 0 16 16" fill="none"><rect x="1.75" y="3.5" width="12.5" height="9" rx="1.5" stroke="currentColor" stroke-width="1.3" /><path d="M1.75 6.5h12.5M4 10h2.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" /></svg></ng-container>
                <ng-container *ngIf="acctKind(a) === 'bank'"><svg viewBox="0 0 16 16" fill="none"><path d="M2 6l6-3.5L14 6M3 13.5h10M4 6.5v5M6.67 6.5v5M9.33 6.5v5M12 6.5v5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" /></svg></ng-container>
              </span>
              {{ a.name }}
              <span class="it-acct-chip-remove" (click)="deselectAccount(a); $event.stopPropagation()">×</span>
            </span>
          </ng-container>
        </div>
        <svg class="it-acct-caret" [class.flipped]="bankDropdownOpen"
          width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>

      <!-- Panel -->
      <div class="it-acct-panel" *ngIf="bankDropdownOpen">
        <!-- Search -->
        <div class="it-acct-search">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="2">
            <circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input #acctSearch type="text" [(ngModel)]="bankSearch"
            placeholder="Search accounts&hellip;" class="it-acct-search-input" />
        </div>

        <!-- Select All -->
        <label class="it-acct-row it-acct-row--all"
          [class.checked]="allBankAccountsSelected">
          <span class="it-acct-check" [class.checked]="allBankAccountsSelected"
            [class.indeterminate]="someBankAccountsSelected">
            <svg *ngIf="allBankAccountsSelected" width="10" height="10" viewBox="0 0 12 12"
              fill="none" stroke="#fff" stroke-width="2.5"><polyline points="2 6 5 9 10 3"></polyline></svg>
            <svg *ngIf="someBankAccountsSelected && !allBankAccountsSelected" width="10" height="10"
              viewBox="0 0 12 12" fill="none" stroke="#fff" stroke-width="2.5"><line x1="2" y1="6" x2="10" y2="6"></line></svg>
          </span>
          <span class="it-acct-name">Select All</span>
          <input type="checkbox" hidden [checked]="allBankAccountsSelected"
            (change)="toggleAllBankAccounts($event.target.checked)" />
        </label>

        <div class="it-acct-divider"></div>

        <!-- Account rows: ten in view, the rest scroll -->
        <div class="it-acct-list">
        <label class="it-acct-row" *ngFor="let acct of filteredBankAccounts"
          [class.checked]="isAccountSelected(acct)">
          <span class="it-acct-check" [class.checked]="isAccountSelected(acct)">
            <svg *ngIf="isAccountSelected(acct)" width="10" height="10" viewBox="0 0 12 12"
              fill="none" stroke="#fff" stroke-width="2.5"><polyline points="2 6 5 9 10 3"></polyline></svg>
          </span>
          <span class="it-acct-icon" [class.it-acct-icon--card]="acctKind(acct) === 'card'" aria-hidden="true">
            <ng-container *ngIf="acctKind(acct) === 'card'"><svg viewBox="0 0 16 16" fill="none"><rect x="1.75" y="3.5" width="12.5" height="9" rx="1.5" stroke="currentColor" stroke-width="1.3" /><path d="M1.75 6.5h12.5M4 10h2.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" /></svg></ng-container>
            <ng-container *ngIf="acctKind(acct) === 'bank'"><svg viewBox="0 0 16 16" fill="none"><path d="M2 6l6-3.5L14 6M3 13.5h10M4 6.5v5M6.67 6.5v5M9.33 6.5v5M12 6.5v5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" /></svg></ng-container>
          </span>
          <span class="it-acct-text">
            <span class="it-acct-name">{{ acct.name }}</span>
            <span class="it-acct-detail">{{ acctDetail(acct) }}</span>
          </span>
          <input type="checkbox" hidden [checked]="isAccountSelected(acct)"
            (change)="toggleAccount(acct, $event.target.checked)" />
        </label>

        <div *ngIf="filteredBankAccounts.length === 0" class="it-acct-empty">
          No accounts match "{{ bankSearch }}"
        </div>
        </div>

        <div class="it-acct-footer">
          <span class="it-acct-footer-count">
            {{ selectedBankAccounts.length }} selected
          </span>
          <button class="it-acct-done" (click)="bankDropdownOpen = false" type="button">Done</button>
        </div>
      </div>
    </section>

    <!-- The daily sync: the same Xero pull, run every morning instead of once. A paid feature,
         switched on the way Inbox Automator is from the Emails tab; the dialog does the rest. -->
    <section class="it-xero-section">
      <div class="it-xero-section-head">
        <span class="it-xero-section-title">Keep in sync</span>
      </div>
      <div class="it-sync-row" [class.it-sync-row--on]="dailySyncMode === 'active'">
        <div class="it-sync-icon" aria-hidden="true">
          <svg viewBox="0 0 20 20" fill="none">
            <path d="M16 10a6 6 0 1 1-1.76-4.24" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
            <path d="M16 4v3h-3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>
        <div class="it-sync-text">
          <div class="it-sync-name">
            Daily sync
            <span class="it-sync-pill" [ngClass]="'it-sync-pill--' + dailySyncMode" *ngIf="dailySyncState">
              <span class="it-sync-dot" *ngIf="dailySyncMode === 'active'"></span>{{ dailySyncPillLabel }}
            </span>
          </div>
          <div class="it-sync-desc">{{ dailySyncDescription }}</div>
        </div>
        <button type="button" class="it-sync-btn" (click)="openDailySync()">{{ dailySyncActionLabel }}</button>
      </div>
    </section>

    <!-- Pinned to the bottom like the review step's, with what Fetch will ask Xero for. -->
    <div class="it-review-footer it-xero-footer">
      <div class="it-review-summary">
        <span>{{ xeroAccountsSummary }}</span>
        <span class="it-review-summary-total" *ngIf="xeroRangeLabel">{{ xeroRangeLabel }}</span>
      </div>
      <button type="button" class="it-primary-btn" (click)="importFromXero()"
              [disabled]="!canFetchFromXero">
        Fetch transactions
      </button>
    </div>
    </ng-container>

    <!-- Loading state — replaces the form while we wait on Xero -->
    <div *ngIf="isXeroLoading" class="it-fetching-state">
      <div class="it-fetching-status">
        <span class="it-btn-spinner it-btn-spinner--dark"></span>
        <span>Fetching transactions from Xero&hellip;</span>
      </div>
      <div class="it-skeleton-wrap">
        <div class="it-skeleton-row" *ngFor="let i of [1,2,3,4,5,6,7,8]"></div>
      </div>
    </div>
  </div>

  <!-- ══════════════════════════════════════════════════════════
       STEP: REFRESH -- asking Xero what is new and what has changed
  ══════════════════════════════════════════════════════════ -->
  <div *ngIf="step === 'refresh'" class="it-body">
    <div class="it-fetching-state" *ngIf="!refreshError">
      <div class="it-fetching-status">
        <span class="it-btn-spinner it-btn-spinner--dark"></span>
        <span>Checking Xero for new transactions and changes&hellip;</span>
      </div>
      <div class="it-skeleton-wrap">
        <div class="it-skeleton-row" *ngFor="let i of [1,2,3,4,5,6]"></div>
      </div>
    </div>
    <div class="it-issues-banner" *ngIf="refreshError">
      {{ refreshError }}
      <button type="button" class="it-override-btn" (click)="startRefresh()">Try again</button>
    </div>
  </div>

  <!-- ══════════════════════════════════════════════════════════
       STEP: SPREADSHEET UPLOAD
  ══════════════════════════════════════════════════════════ -->
  <div *ngIf="step === 'spreadsheet'" class="it-body">

    <!-- Template download (hide once a file is selected) -->
    <div class="it-template-banner" *ngIf="!fileObj">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
        <line x1="12" y1="18" x2="12" y2="12"></line>
        <line x1="9" y1="15" x2="15" y2="15"></line>
      </svg>
      <div>
        <div class="it-template-title">Required columns</div>
        <div class="it-template-cols">Date &bull; Description &bull; Spent &bull; Received &bull; Account Name</div>
      </div>
      <a href="../../../../assets/SampleUnreconciled.csv" download class="it-template-link">
        Download template
      </a>
    </div>

    <!-- Drop zone -->
    <ngx-file-drop (onFileDrop)="onFileDropped($event)" [accept]="'.csv'">
      <ng-template ngx-file-drop-content-tmp let-openFileSelector="openFileSelector">
        <input hidden #fileInput type="file" accept=".csv" (change)="onFileSelected($event)" />

        <div *ngIf="!fileObj && !isFileUploading" class="it-dropzone" (click)="fileInput.click()">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#d0d5dd" stroke-width="1.5">
            <polyline points="16 16 12 12 8 16"></polyline>
            <line x1="12" y1="12" x2="12" y2="21"></line>
            <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"></path>
          </svg>
          <p><span class="it-link">Click to upload</span> or drag and drop</p>
          <p class="it-dropzone-hint">CSV files only</p>
        </div>

        <div *ngIf="fileObj && !isFileUploading" class="it-file-preview">
          <div class="it-file-info">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#f6ca58" stroke-width="1.5">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
            </svg>
            <div>
              <div class="it-file-name">{{ fileObj.name }}</div>
              <div class="it-file-size">{{ fileObj.size | number:'1.2-2' }} MB</div>
            </div>
          </div>
          <button type="button" class="it-file-remove" (click)="removeFile()" title="Remove file">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div *ngIf="isFileUploading" class="it-processing">
          <div class="it-processing-spinner"></div>
          <span>Processing file&hellip;</span>
        </div>
      </ng-template>
    </ngx-file-drop>

    <!-- Inline error message -->
    <div *ngIf="uploadError" class="it-upload-error">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
      <span>{{ uploadError }}</span>
    </div>

    <button type="button" class="it-primary-btn" (click)="processSpreadsheet()"
      [disabled]="!fileObj || isFileUploading">
      <span *ngIf="!isFileUploading">Process File</span>
      <span *ngIf="isFileUploading">
        <span class="it-btn-spinner"></span> Processing&hellip;
      </span>
    </button>
  </div>

  <!-- ══════════════════════════════════════════════════════════
       STEP: MAP COLUMNS
  ══════════════════════════════════════════════════════════ -->
  <div *ngIf="step === 'map-columns'" class="it-body">

    <div class="it-map-info">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
      <span>Some columns in your file don't match the expected format. Please map each required column to a column from your file.</span>
    </div>

    <div class="it-map-list">
      <div class="it-map-row" *ngFor="let m of columnMappings">
        <div class="it-map-required">
          <span class="it-map-label">{{ m.required }}</span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="2">
            <line x1="5" y1="12" x2="19" y2="12"></line>
            <polyline points="12 5 19 12 12 19"></polyline>
          </svg>
        </div>
        <select class="it-map-select" [(ngModel)]="m.mapped" [class.it-map-select--empty]="!m.mapped"
          [title]="'Map ' + m.required + ' column'">
          <option [ngValue]="null" disabled>Select column…</option>
          <option *ngFor="let h of csvHeaders" [ngValue]="h"
            [disabled]="isHeaderTaken(h, m)">
            {{ h }}
          </option>
        </select>
      </div>
    </div>

    <!-- Inline error message -->
    <div *ngIf="uploadError" class="it-upload-error">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
      <span>{{ uploadError }}</span>
    </div>

    <button type="button" class="it-primary-btn" (click)="applyColumnMappingAndUpload()"
      [disabled]="!allColumnsMapped || isFileUploading">
      <span *ngIf="!isFileUploading">Continue</span>
      <span *ngIf="isFileUploading">
        <span class="it-btn-spinner"></span> Processing&hellip;
      </span>
    </button>
  </div>

  <!-- ══════════════════════════════════════════════════════════
       STEP: MAP ACCOUNTS
  ══════════════════════════════════════════════════════════ -->
  <div *ngIf="step === 'map-accounts'" class="it-body">

    <div class="it-map-info">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
      <span>Some account names in your file don't match your organisation's accounts. Please assign each to an existing account.</span>
    </div>

    <div class="it-map-list">
      <div class="it-map-row" *ngFor="let m of accountMappings">
        <div class="it-map-required">
          <span class="it-map-label it-map-label--file">"{{ m.csvName }}"</span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="2">
            <line x1="5" y1="12" x2="19" y2="12"></line>
            <polyline points="12 5 19 12 12 19"></polyline>
          </svg>
        </div>
        <select class="it-map-select" [(ngModel)]="m.mappedAccountName" [class.it-map-select--empty]="!m.mappedAccountName"
          [title]="'Map ' + m.csvName + ' to an account'">
          <option value="" disabled>Select account…</option>
          <option *ngFor="let acct of bankAccountList" [value]="acct.name">{{ acct.name }}</option>
        </select>
      </div>
    </div>

    <button type="button" class="it-primary-btn" (click)="applyAccountMappingAndReview()"
      [disabled]="!allAccountsMapped">
      Continue to Review
    </button>
  </div>

  <!-- ══════════════════════════════════════════════════════════
       STEP: REVIEW
  ══════════════════════════════════════════════════════════ -->
  <div *ngIf="step === 'review'" class="it-body it-body--review">

    <!-- What this import holds, at a glance: reconciled rows (Xero sends them too, and they
         import as reconciled) apart from the ones still to reconcile. Each tile filters the list. -->
    <!-- Refresh: statuses Xero disagrees with, reviewed and ticked like the new transactions. -->
    <section class="it-changes" *ngIf="source === 'Refresh' && refreshChanges.length">
      <h3 class="it-changes-title">
        Status changes <span class="it-changes-count">{{ refreshChanges.length }}</span>
      </h3>
      <div class="it-change-group" *ngFor="let group of refreshChangeGroups">
        <label class="it-change-group-head">
          <input type="checkbox" [checked]="isChangeGroupAllSelected(group.rows)"
                 [indeterminate]="isChangeGroupIndeterminate(group.rows)"
                 (change)="toggleChangeGroup(group.rows, $event.target.checked)"
                 [attr.aria-label]="'Select all: ' + group.title" />
          <span class="it-change-group-title">{{ group.title }}</span>
          <span class="it-change-group-count">{{ group.rows.length }}</span>
        </label>
        <p class="it-change-group-hint">{{ group.hint }}</p>
        <div class="it-change-row" *ngFor="let row of group.rows; trackBy: trackByChange"
             [class.it-change-row--locked]="!row.canApply">
          <input type="checkbox" [(ngModel)]="row.selected" [disabled]="!row.canApply"
                 [attr.aria-label]="'Apply: ' + row.description" />
          <span class="it-change-main">
            <span class="it-change-desc" [title]="row.description">{{ row.description || '—' }}</span>
            <span class="it-change-meta">
              {{ displayTransactionDate(row.transactionDate) }} &middot; {{ row.accountName }}
              <span class="it-change-reason" *ngIf="row.reason"> &middot; {{ row.reason }}</span>
            </span>
          </span>
          <span class="it-change-side">
            <span class="it-change-amount" [class.it-amount-negative]="row.amount < 0">
              {{ row.amount < 0 ? '(' + ((row.amount * -1) | number:'1.2-2') + ')' : (row.amount | number:'1.2-2') }}
            </span>
            <span class="it-change-pill" [ngClass]="'it-change-pill--' + row.change.toLowerCase()">
              <ng-container [ngSwitch]="row.change">
                <ng-container *ngSwitchCase="'Reconciled'">To reconcile &rarr; Reconciled</ng-container>
                <ng-container *ngSwitchCase="'Unreconciled'">Reconciled &rarr; To reconcile</ng-container>
                <ng-container *ngSwitchDefault>Remove</ng-container>
              </ng-container>
            </span>
          </span>
        </div>
      </div>
    </section>

    <h3 class="it-changes-title" *ngIf="source === 'Refresh' && allTransactions.length">
      New transactions <span class="it-changes-count">{{ allTransactions.length }}</span>
    </h3>

    <ng-container *ngIf="source !== 'Refresh' || allTransactions.length">
    <div class="it-summary" role="group" aria-label="Show">
      <button type="button" class="it-summary-tile" [class.is-on]="statusFilter === 'all'" (click)="setStatusFilter('all')">
        <span class="it-summary-value">{{ allTransactions.length }}</span>
        <span class="it-summary-label">Transactions</span>
      </button>
      <button type="button" class="it-summary-tile it-summary-tile--todo" [class.is-on]="statusFilter === 'toReconcile'"
              (click)="setStatusFilter('toReconcile')" [disabled]="toReconcileCount === 0">
        <span class="it-summary-value">{{ toReconcileCount }}</span>
        <span class="it-summary-label"><span class="it-dot it-dot--todo"></span>To reconcile</span>
      </button>
      <button type="button" class="it-summary-tile it-summary-tile--done" [class.is-on]="statusFilter === 'reconciled'"
              (click)="setStatusFilter('reconciled')" [disabled]="reconciledCount === 0">
        <span class="it-summary-value">{{ reconciledCount }}</span>
        <span class="it-summary-label"><span class="it-dot it-dot--done"></span>Already reconciled</span>
      </button>
    </div>

    <!-- Report selector — single combobox that can select an existing report
         OR create a new one inline. The status pill below clarifies which
         action the current selection will perform. -->
    <div class="it-field-group it-report-selector">
      <label class="it-label" for="ddlImportReport">Expense report</label>
      <ng-select
        [(ngModel)]="selectedReport"
        [items]="selectableReports"
        [searchable]="true"
        [editableSearchTerm]="true"
        [clearable]="reportOptional"
        [addTag]="addReportTag"
        bindLabel="reportName"
        addTagText="Create report:"
        [placeholder]="reportOptional ? 'None' : 'Select a report or type a new name'"
        class="it-ng-select"
        [ngModelOptions]="{standalone: true}"
        id="ddlImportReport">
        <ng-template ng-option-tmp let-item="item">
          <div class="it-report-option">
            <svg class="it-report-option-icon" width="14" height="14" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" stroke-width="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
            </svg>
            <span class="it-report-option-name">{{ item.reportName }}</span>
            <span class="it-report-option-meta">In progress</span>
          </div>
        </ng-template>
        <ng-template ng-label-tmp let-item="item">
          {{ item.reportName }}
        </ng-template>
        <ng-template ng-tag-tmp let-search="searchTerm">
          <div class="it-report-create">
            <span class="it-report-create-icon">+</span>
            Create new report: <strong>"{{ search }}"</strong>
          </div>
        </ng-template>
      </ng-select>
      <p class="it-report-hint">{{ reportHint }}</p>
    </div>



    <!-- Already-in-report banner — locked by default, but each row offers an override -->
    <div *ngIf="alreadyExistsCount > 0" class="it-issues-banner it-issues-banner--info">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"></circle>
        <polyline points="9 12 11 14 15 10"></polyline>
      </svg>
      <span *ngIf="source !== 'BankFeed'">
        {{ alreadyExistsCount }} row{{ alreadyExistsCount !== 1 ? 's have' : ' has' }} already been imported.
        <ng-container *ngIf="overriddenCount === 0">
          Click &ldquo;Import anyway&rdquo; on a row to override.
        </ng-container>
        <ng-container *ngIf="overriddenCount > 0">
          {{ overriddenCount }} of {{ alreadyExistsCount }} will be imported as duplicate{{ overriddenCount !== 1 ? 's' : '' }}.
        </ng-container>
      </span>
      <!-- Same Stripe transaction, already a row: importing it again would only duplicate it. -->
      <span *ngIf="source === 'BankFeed'">
        {{ alreadyExistsCount }} transaction{{ alreadyExistsCount !== 1 ? 's were' : ' was' }} already imported
        from this bank feed and {{ alreadyExistsCount !== 1 ? 'are' : 'is' }} shown for reference.
      </span>
    </div>

    <!-- Rows imported earlier that the bank has since changed or voided: applied on import. -->
    <div *ngIf="source === 'BankFeed' && bankPreviewChangeCount > 0" class="it-issues-banner it-issues-banner--info">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="16" x2="12" y2="12"></line>
        <line x1="12" y1="8" x2="12.01" y2="8"></line>
      </svg>
      <span>
        The bank has changed {{ bankPreviewChangeCount }} transaction{{ bankPreviewChangeCount !== 1 ? 's' : '' }}
        imported earlier. {{ bankPreviewChangeCount !== 1 ? 'They' : 'It' }} will be updated when you import.
      </span>
    </div>

    <!-- Other validation issues banner -->
    <div *ngIf="otherIssuesCount > 0" class="it-issues-banner">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
      {{ otherIssuesCount }} row{{ otherIssuesCount !== 1 ? 's have' : ' has' }} issues — unchecked by default but you can still select and import them.
    </div>

    <!-- Search bar -->
    <div class="it-search-bar">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="2">
        <circle cx="11" cy="11" r="8"></circle>
        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
      </svg>
      <input type="text" [(ngModel)]="searchQuery" (ngModelChange)="rebuildGroupedTransactions()" placeholder="Search transactions&hellip;"
        class="it-search-input" />
      <button *ngIf="searchQuery" type="button" class="it-search-clear" (click)="clearSearch()" title="Clear search">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>

    <!-- ── All transactions table (grouped by account) ── -->
    <div class="it-table-wrap">
      <table class="it-table" *ngIf="groupedTransactions.length > 0; else noTransactions">
        <thead>
          <tr>
            <th class="it-col-check" scope="col">
              <input type="checkbox" [checked]="allSelected" [indeterminate]="indeterminate"
                (change)="toggleAll($event.target.checked)" title="Select all"
                aria-label="Select all transactions" />
            </th>
            <th class="it-col-desc">Description</th>
            <th class="it-col-status">Status</th>
            <th class="it-col-date">Date</th>
            <th class="it-col-amount">Amount</th>
          </tr>
        </thead>
        <tbody *ngFor="let group of groupedTransactions; trackBy: trackByGroup">
          <!-- Group header row -->
          <tr class="it-group-row" (click)="toggleGroup(group.accountName)">
            <td class="it-col-check" (click)="$event.stopPropagation()">
              <input type="checkbox"
                [checked]="isGroupAllSelected(group.transactions)"
                [indeterminate]="isGroupIndeterminate(group.transactions)"
                (change)="toggleGroupAll(group.transactions, $event.target.checked)" />
            </td>
            <td colspan="4">
              <div class="it-group-header">
                <svg class="it-group-chevron" [class.it-group-chevron--collapsed]="isGroupCollapsed(group.accountName)"
                  width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
                <span class="it-group-name">{{ group.accountName }}</span>
                <span class="it-group-meta">
                  {{ group.transactions.length }} transaction{{ group.transactions.length !== 1 ? 's' : '' }}
                  &bull;
                  <span [class.it-amount-positive]="groupTotal(group.transactions) > 0"
                        [class.it-amount-negative]="groupTotal(group.transactions) < 0">
                    {{ groupTotal(group.transactions) | number:'1.2-2' }}
                  </span>
                </span>
              </div>
            </td>
          </tr>
          <!-- Transaction rows -->
          <ng-container *ngIf="!isGroupCollapsed(group.accountName)">
            <tr *ngFor="let tx of group.transactions; trackBy: trackByTx"
              [class.it-row-selected]="tx.isImport"
              [class.it-row-issue]="tx.importStatus !== 'success' && !isAlreadyExists(tx)"
              [class.it-row-disabled]="!isReimportable(tx)"
              [class.it-row-overridden]="isAlreadyExists(tx) && isOverridden(tx)">
              <td class="it-col-check">
                <input type="checkbox" [(ngModel)]="tx.isImport"
                  [disabled]="!isReimportable(tx)"
                  [title]="!isReimportable(tx) ? 'Already imported — click Import anyway to override' : 'Select ' + tx.description"
                  [attr.aria-label]="'Select ' + tx.description" />
              </td>
              <td class="it-col-desc">
                <div class="it-desc-text" [title]="tx.description">{{ tx.description || '—' }}</div>

                <!-- Already-exists rows: locked by default, with a per-row override action. -->
                <div *ngIf="isAlreadyExists(tx) && source === 'BankFeed'" class="it-inline-issue it-inline-issue--info">
                  <span>Already imported from this bank feed</span>
                </div>
                <div *ngIf="isAlreadyExists(tx) && !isOverridden(tx) && source !== 'BankFeed'" class="it-inline-issue it-inline-issue--info">
                  <span>Already imported</span>
                  <button type="button" class="it-override-btn" (click)="toggleOverride(tx)"
                    title="Import this row even though it has already been imported">
                    Import anyway
                  </button>
                </div>
                <div *ngIf="isAlreadyExists(tx) && isOverridden(tx)" class="it-inline-issue it-inline-issue--override">
                  <span>Will import as duplicate</span>
                  <button type="button" class="it-override-btn it-override-btn--undo" (click)="toggleOverride(tx)"
                    title="Undo override and lock this row again">
                    Undo
                  </button>
                </div>


                <!-- Other validation issues — unchanged path. -->
                <div *ngIf="tx.importStatus !== 'success' && !isAlreadyExists(tx)" class="it-inline-issue">
                  <ng-container *ngIf="tx.validationErrors?.length; else legacyErr">
                    <span *ngFor="let err of tx.validationErrors; let last = last">{{ err.field }}: {{ err.message }}{{ !last ? ' · ' : '' }}</span>
                  </ng-container>
                  <ng-template #legacyErr>{{ tx.importStatus }}</ng-template>
                </div>
              </td>
              <!-- Xero sends reconciled rows too; they import as reconciled, the rest to reconcile. -->
              <td class="it-col-status">
                <span class="it-status it-status--done" *ngIf="tx.isReconciled" title="Reconciled in Xero: imports as reconciled">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"
                       stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>
                  Reconciled
                </span>
                <span class="it-status it-status--todo" *ngIf="!tx.isReconciled" title="Imports as a transaction to reconcile">
                  <span class="it-dot it-dot--todo"></span>To reconcile
                </span>
              </td>
              <td class="it-col-date">{{ displayTransactionDate(tx.transactionDate) }}</td>
              <td class="it-col-amount">
                <span *ngIf="tx.amount > 0" class="it-amount-positive">{{ tx.amount | number:'1.2-2' }}</span>
                <span *ngIf="tx.amount < 0" class="it-amount-negative">({{ (tx.amount * -1) | number:'1.2-2' }})</span>
                <span *ngIf="!tx.amount">—</span>
              </td>
            </tr>
          </ng-container>
        </tbody>
      </table>
      <ng-template #noTransactions>
        <div class="it-empty-state">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d0d5dd" stroke-width="1.5">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <p>No transactions{{ searchQuery ? ' matching your search' : '' }}.</p>
        </div>
      </ng-template>
    </div>

    </ng-container>

    <!-- ── Confirm footer ── -->
    <div class="it-review-footer" *ngIf="source === 'Refresh'">
      <div class="it-review-summary">
        <span>
          {{ selectedCount }} new &middot; {{ selectedChangeCount }} status change{{ selectedChangeCount !== 1 ? 's' : '' }} selected
        </span>
      </div>
      <button type="button" class="it-primary-btn" (click)="confirmImport()" [disabled]="isSaving || !canConfirmImport">
        <span *ngIf="isSaving" class="it-btn-spinner"></span>
        <span *ngIf="!isSaving">{{ refreshApplyLabel }}</span>
        <span *ngIf="isSaving">Applying&hellip;</span>
      </button>
    </div>

    <div class="it-review-footer" *ngIf="source !== 'Refresh'">
      <div class="it-review-summary">
        <span>
          {{ selectedCount }} of {{ allTransactions.length }} selected
          <span class="it-review-split" *ngIf="selectedCount > 0">
            · {{ selectedToReconcileCount }} to reconcile · {{ selectedReconciledCount }} already reconciled
          </span>
        </span>
        <span class="it-review-summary-total" *ngIf="selectedCount > 0">{{ selectedTotal | number:'1.2-2' }}</span>
      </div>
      <button type="button" class="it-primary-btn" (click)="confirmImport()"
        [disabled]="isSaving || !canConfirmImport">
        <span *ngIf="isSaving" class="it-btn-spinner"></span>
        <span *ngIf="!isSaving">Import {{ selectedCount }} Transaction{{ selectedCount !== 1 ? 's' : '' }}</span>
        <span *ngIf="isSaving">Importing&hellip;</span>
      </button>
    </div>
  </div>

</div>
